import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Divider,
  Autocomplete,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import InvoiceService, { Invoice, InvoiceItem } from '../../services/invoice.service';
import { insuranceClientService } from '../../services/insuranceClientService';
import { insuranceCompanyService } from '../../services/insuranceCompanyService';
import { serviceProviderService } from '../../services/serviceProviderService';
import CustomerService from '../../services/customer.service';
import ProductService from '../../services/product.service';
import { NumberTextField } from '../../components/common';

interface LineItem {
  tempId: string;
  id?: number;
  product: any;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  tax: number;
  total: number;
}

// Map backend-only invoice types to form dropdown values so edit page shows correct type
const BACKEND_INVOICE_TYPE_TO_FORM: Record<string, string> = {
  insurance_company_monthly: 'insurance',
  service_provider_monthly: 'service_provider',
  service_provider_to_darb: 'service_provider_to_darb',
};

const InvoiceForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Insurance Clients
  const [insuranceClients, setInsuranceClients] = useState<any[]>([]);
  const [insuranceClientsLoading, setInsuranceClientsLoading] = useState(false);
  const [insuranceClientSearchInput, setInsuranceClientSearchInput] = useState('');
  const [insuranceClientsSearchResults, setInsuranceClientsSearchResults] = useState<any[]>([]);
  const [insuranceClientsSearching, setInsuranceClientsSearching] = useState(false);
  const insuranceClientSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedInsuranceClient, setSelectedInsuranceClient] = useState<any>(null);
  
  // Insurance Companies
  const [insuranceCompanies, setInsuranceCompanies] = useState<any[]>([]);
  const [insuranceCompaniesLoading, setInsuranceCompaniesLoading] = useState(false);
  const [selectedInsuranceCompany, setSelectedInsuranceCompany] = useState<any>(null);
  
  // Service Providers
  const [serviceProviders, setServiceProviders] = useState<any[]>([]);
  const [serviceProvidersLoading, setServiceProvidersLoading] = useState(false);
  const [selectedServiceProvider, setSelectedServiceProvider] = useState<any>(null);
  
  // Customers
  const [customers, setCustomers] = useState<any[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  
  const [products, setProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  const [formData, setFormData] = useState<Invoice>({
    invoiceNumber: '', // Auto-generated on save (INV-YYYY-MM-####)
    customer: 0,
    insuranceClient: 0,
    serviceProvider: 0,
    insuranceCompany: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days
    subtotal: 0,
    tax: 0,
    discount: 0,
    total: 0,
    paidAmount: 0,
    balanceDue: 0,
    status: 'draft',
    invoiceType: 'customer',
    taxRate: 0.05,
    other: 0,
    omTax: 0,
    omanTaxId: '',
    notes: '',
  });

  useEffect(() => {
    fetchInsuranceClients();
    fetchInsuranceCompanies();
    fetchServiceProviders();
    fetchCustomers();
    fetchProducts();
    if (isEdit && id) {
      fetchInvoice(parseInt(id));
    }
  }, [id, isEdit]);

  // Refetch entity lists when window regains focus (e.g. after creating one in new tab)
  useEffect(() => {
    const onFocus = () => {
      fetchInsuranceClients();
      fetchInsuranceCompanies();
      fetchServiceProviders();
      fetchCustomers();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // When editing: set selectedInsuranceCompany once insurance companies have loaded (fixes race where fetchInvoice runs before insuranceCompanies is populated)
  useEffect(() => {
    if (!isEdit || !formData.insuranceCompany || insuranceCompanies.length === 0) return;
    const company = insuranceCompanies.find(
      (c) => (c.attributes?.name || c.name) === formData.insuranceCompany
    );
    if (company) {
      setSelectedInsuranceCompany((prev: any) =>
        prev && (prev.attributes?.name || prev.name) === formData.insuranceCompany ? prev : company
      );
    }
  }, [isEdit, formData.insuranceCompany, insuranceCompanies]);

  // When editing: set selectedCustomer from customers list by id (so dropdown shows value even if API returned different object reference or list loaded after invoice)
  useEffect(() => {
    if (!isEdit || !formData.customer || customers.length === 0) return;
    const customer = customers.find((c) => c.id === formData.customer);
    if (customer) {
      setSelectedCustomer((prev: any) => (prev?.id === formData.customer ? prev : customer));
    }
  }, [isEdit, formData.customer, customers]);

  // When editing: set selectedInsuranceClient from list by id
  useEffect(() => {
    if (!isEdit || !formData.insuranceClient || insuranceClients.length === 0) return;
    const client = insuranceClients.find((c) => c.id === formData.insuranceClient);
    if (client) {
      setSelectedInsuranceClient((prev: any) => (prev?.id === formData.insuranceClient ? prev : client));
    }
  }, [isEdit, formData.insuranceClient, insuranceClients]);

  // When editing: set selectedServiceProvider from list by id
  useEffect(() => {
    if (!isEdit || !formData.serviceProvider || serviceProviders.length === 0) return;
    const provider = serviceProviders.find((p) => p.id === formData.serviceProvider);
    if (provider) {
      setSelectedServiceProvider((prev: any) => (prev?.id === formData.serviceProvider ? prev : provider));
    }
  }, [isEdit, formData.serviceProvider, serviceProviders]);

  const fetchInsuranceClients = async () => {
    try {
      setInsuranceClientsLoading(true);
      const response = await insuranceClientService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 250,
      });
      setInsuranceClients(response.data || []);
    } catch (error) {
      console.error('Error fetching insurance clients:', error);
    } finally {
      setInsuranceClientsLoading(false);
    }
  };

  const searchInsuranceClients = async (query: string) => {
    const trimmed = (query || '').trim();
    if (!trimmed) {
      setInsuranceClientsSearchResults([]);
      return;
    }
    try {
      setInsuranceClientsSearching(true);
      const params: Record<string, string> = {
        'filters[isActive][$eq]': 'true',
        'pagination[pageSize]': '50',
        'filters[$or][0][vinNumber][$containsi]': trimmed,
        'filters[$or][1][customerName][$containsi]': trimmed,
        'filters[$or][2][phoneNumber][$containsi]': trimmed,
        'filters[$or][3][plateNumber][$containsi]': trimmed,
        'filters[$or][4][policyNumber][$containsi]': trimmed,
      };
      const response = await insuranceClientService.getAll(params);
      setInsuranceClientsSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching insurance clients:', error);
      setInsuranceClientsSearchResults([]);
    } finally {
      setInsuranceClientsSearching(false);
    }
  };

  const fetchInsuranceCompanies = async () => {
    try {
      setInsuranceCompaniesLoading(true);
      const response = await insuranceCompanyService.getAll();
      setInsuranceCompanies(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching insurance companies:', error);
    } finally {
      setInsuranceCompaniesLoading(false);
    }
  };

  const fetchServiceProviders = async () => {
    try {
      setServiceProvidersLoading(true);
      const response = await serviceProviderService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
      });
      setServiceProviders(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Error fetching service providers:', error);
    } finally {
      setServiceProvidersLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      setCustomersLoading(true);
      const response = await CustomerService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
      });
      setCustomers(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await ProductService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
        populate: 'category',
      });
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchInvoice = async (invoiceId: number) => {
    try {
      setLoading(true);
      const response = await InvoiceService.getOne(invoiceId);
      const invoiceData = response.data?.data;

      if (invoiceData) {
        const attrs = invoiceData.attributes;
        const backendInvoiceType = attrs.invoiceType || 'customer';
        const formInvoiceType = BACKEND_INVOICE_TYPE_TO_FORM[backendInvoiceType] || backendInvoiceType;
        // For insurance type: derive insurance company from insurance client when invoice has none stored
        const insuranceCompanyFromClient = attrs.insuranceClient?.data?.attributes?.insuranceCompany?.data?.attributes?.name
          || attrs.insuranceClient?.data?.attributes?.insuranceCompany?.data?.name
          || (typeof attrs.insuranceClient?.data?.attributes?.insuranceCompany === 'string' ? attrs.insuranceClient.data.attributes.insuranceCompany : null);
        const insuranceCompanyValue = attrs.insuranceCompany || insuranceCompanyFromClient || '';
        setFormData({
          invoiceNumber: attrs.invoiceNumber,
          customer: attrs.customer?.data?.id || 0,
          insuranceClient: attrs.insuranceClient?.data?.id || 0,
          serviceProvider: attrs.serviceProvider?.data?.id || 0,
          insuranceCompany: insuranceCompanyValue,
          salesOrder: attrs.salesOrder?.data?.id,
          invoiceDate: attrs.invoiceDate,
          dueDate: attrs.dueDate || '',
          subtotal: attrs.subtotal || 0,
          tax: attrs.tax || 0,
          discount: attrs.discount || 0,
          total: attrs.total || 0,
          paidAmount: attrs.paidAmount || 0,
          balanceDue: attrs.balanceDue || 0,
          status: attrs.status || 'draft',
          invoiceType: formInvoiceType as Invoice['invoiceType'],
          taxRate: attrs.taxRate || 0.05,
          other: attrs.other || 0,
          omTax: attrs.omTax ?? 0,
          omanTaxId: attrs.omanTaxId ?? '',
          notes: attrs.notes || '',
        });

        if (attrs.insuranceClient?.data) {
          setSelectedInsuranceClient(attrs.insuranceClient.data);
        }
        
        if (attrs.customer?.data) {
          setSelectedCustomer(attrs.customer.data);
        }
        
        if (attrs.serviceProvider?.data) {
          setSelectedServiceProvider(attrs.serviceProvider.data);
        }
        // Insurance company: from invoice field; if missing for insurance type, fetch from insurance client
        const nestedCompany = attrs.insuranceClient?.data?.attributes?.insuranceCompany?.data;
        if (nestedCompany) {
          setSelectedInsuranceCompany(nestedCompany);
        } else if (insuranceCompanyValue) {
          // selectedInsuranceCompany set in useEffect when insuranceCompanies loads (match by name)
        } else if (formInvoiceType === 'insurance' && attrs.insuranceClient?.data?.id) {
          // Fallback: fetch insurance client with company (avoids nested populate on invoice which can 500)
          insuranceClientService.getOne(attrs.insuranceClient.data.id).then((client: any) => {
            const companyRel = client?.attributes?.insuranceCompany ?? client?.insuranceCompany;
            const companyData = companyRel?.data ?? companyRel;
            const companyName = companyData?.attributes?.name ?? companyData?.name;
            if (companyName) {
              setFormData((prev) => ({ ...prev, insuranceCompany: companyName }));
              if (companyData) setSelectedInsuranceCompany(companyData);
            }
          }).catch(() => {});
        }

        // Fetch line items
        const itemsResponse = await InvoiceService.getItems(invoiceId);
        const items = itemsResponse.data?.data || [];
        
        const mappedItems: LineItem[] = items.map((item: any) => ({
          tempId: `item-${item.id}`,
          id: item.id,
          product: item.attributes?.product?.data,
          description: item.attributes?.description || '',
          quantity: item.attributes?.quantity || 1,
          unitPrice: item.attributes?.unitPrice || 0,
          discount: item.attributes?.discount || 0,
          tax: item.attributes?.tax || 0,
          total: item.attributes?.total || 0,
        }));
        
        setLineItems(mappedItems);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const numFields = ['subtotal', 'tax', 'discount', 'total', 'paidAmount', 'balanceDue', 'taxRate', 'other', 'omTax'];
    const processedValue = numFields.includes(name) ? parseFloat(value) || 0 : value;
    setFormData((prev) => ({ ...prev, [name]: processedValue }));
  };


  const addLineItem = () => {
    const newItem: LineItem = {
      tempId: `temp-${Date.now()}`,
      product: null,
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      tax: 0,
      total: 0,
    };
    setLineItems([...lineItems, newItem]);
  };

  const removeLineItem = (tempId: string) => {
    setLineItems(lineItems.filter((item) => item.tempId !== tempId));
  };

  const updateLineItem = (tempId: string, field: keyof LineItem, value: any) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.tempId === tempId) {
          const updated = { ...item, [field]: value };
          
          // Product: either select from list (object) or custom text (string)
          if (field === 'product') {
            if (typeof value === 'string') {
              updated.product = null;
              updated.description = value.trim() || '';
            } else if (value && typeof value === 'object') {
              updated.product = value;
              updated.unitPrice = Number(value.attributes?.price) || 0;
              updated.description = value.attributes?.name || '';
            } else {
              updated.product = null;
              updated.description = '';
            }
          }
          
          // Convert numeric fields to numbers
          if (field === 'quantity' || field === 'unitPrice' || field === 'discount' || field === 'tax') {
            updated[field] = Number(value) || 0;
          }
          
          // Calculate line total (discount cannot exceed line subtotal)
          const qty = Number(updated.quantity) || 0;
          const price = Number(updated.unitPrice) || 0;
          const discountRaw = Number(updated.discount) || 0;
          const tax = Number(updated.tax) || 0;
          const subtotal = qty * price;
          const discount = Math.min(discountRaw, subtotal);
          if (discount !== discountRaw) updated.discount = discount;
          const afterDiscount = subtotal - discount;
          updated.total = Math.max(0, afterDiscount + tax);
          return updated;
        }
        return item;
      })
    );
  };

  // Calculate invoice totals
  useEffect(() => {
    const subtotal = lineItems.reduce((sum, item) => {
      const itemSubtotal = Number(item.quantity) * Number(item.unitPrice);
      return sum + itemSubtotal;
    }, 0);

    const totalDiscountRaw = lineItems.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
    const totalDiscount = Math.min(totalDiscountRaw, subtotal);
    const afterDiscount = Math.max(0, subtotal - totalDiscount);

    const rawRate = formData.taxRate ?? 0.05;
    const taxRate = rawRate > 1 ? rawRate / 100 : rawRate;
    const totalTax = lineItems.reduce((sum, item) => {
      const lineSub = Number(item.quantity) * Number(item.unitPrice);
      const lineDisc = Math.min(Number(item.discount) || 0, lineSub);
      const lineAfter = lineSub - lineDisc;
      const lineTaxVal = Number(item.tax) || 0;
      const effectiveLineTax = lineTaxVal > 0 ? lineTaxVal : lineAfter * taxRate;
      return sum + effectiveLineTax;
    }, 0);

    const other = Number(formData.other) || 0;
    const total = afterDiscount + totalTax + other;
    const balanceDue = total - (formData.paidAmount || 0);

    setFormData((prev) => ({
      ...prev,
      subtotal: Number(subtotal) || 0,
      discount: Number(totalDiscount) || 0,
      tax: Number(totalTax) || 0,
      total: Math.max(0, Number(total) || 0),
      balanceDue: Number(balanceDue) || 0,
    }));
  }, [lineItems, formData.paidAmount, formData.taxRate, formData.other]);

  const validateForm = (): boolean => {
    // Check if entity is selected based on invoice type
    const hasEntity = 
      (formData.invoiceType === 'insurance' && selectedInsuranceCompany) ||
      (formData.invoiceType === 'service_provider' && selectedServiceProvider) ||
      (formData.invoiceType === 'customer' && (selectedCustomer || formData.customerName)) ||
      (formData.invoiceType === 'insurance_customer' && selectedInsuranceClient) ||
      (formData.invoiceType === 'service_provider_to_darb' && selectedServiceProvider && selectedInsuranceClient);
    
    if (!hasEntity) {
      const typeLabels: Record<string, string> = {
        insurance: 'insurance company',
        service_provider: 'service provider',
        customer: 'customer',
        insurance_customer: 'insurance customer',
        service_provider_to_darb: 'service provider and insurance client'
      };
      setError(`Please select a ${typeLabels[formData.invoiceType || 'customer'] || 'client'}`);
      return false;
    }
    if (lineItems.length === 0) {
      setError('Please add at least one line item');
      return false;
    }
    if (lineItems.some(item => !item.product && !(item.description?.trim()))) {
      setError('Each line item must have a product selected or a description entered');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      let invoiceId: number;

      // Prepare invoice data based on type
      const invoiceData: any = {
        ...formData,
        // Clear all entity references first
        insuranceClient: null,
        customer: null,
        customerName: null,
        serviceProvider: null,
        insuranceCompany: null,
      };

      // Set the appropriate entity based on invoice type
      switch (formData.invoiceType) {
        case 'insurance':
          invoiceData.insuranceCompany = selectedInsuranceCompany?.attributes?.name || selectedInsuranceCompany?.name || '';
          break;
        case 'service_provider':
          invoiceData.serviceProvider = selectedServiceProvider?.id || null;
          break;
        case 'customer':
          if (selectedCustomer) {
            invoiceData.customer = selectedCustomer.id;
          } else if (formData.customerName) {
            invoiceData.customerName = formData.customerName;
          }
          break;
        case 'insurance_customer':
          invoiceData.insuranceClient = selectedInsuranceClient?.id || null;
          break;
        case 'service_provider_to_darb':
          invoiceData.serviceProvider = selectedServiceProvider?.id || null;
          invoiceData.insuranceClient = selectedInsuranceClient?.id || null;
          invoiceData.invoiceType = 'service_provider_to_darb';
          break;
      }

      // Create or update invoice
      if (isEdit && id) {
        await InvoiceService.update(parseInt(id), invoiceData);
        invoiceId = parseInt(id);
        setSuccess('Invoice updated!');
      } else {
        // Create: always let backend assign invoice number (never send placeholder or invalid)
        invoiceData.invoiceNumber = '';
        const response = await InvoiceService.create(invoiceData);
        invoiceId = response.data?.data?.id;
        setSuccess('Invoice created!');
      }

      // Save line items
      if (invoiceId) {
        // Delete old items if editing
        if (isEdit) {
          const oldItemsResponse = await InvoiceService.getItems(invoiceId);
          const oldItems = oldItemsResponse.data?.data || [];
          await Promise.all(
            oldItems.map((item: any) => InvoiceService.deleteItem(item.id))
          );
        }

        // Create new items (use effective line tax/total when line tax is 0)
        const rawRate = formData.taxRate ?? 0.05;
        const taxRate = rawRate > 1 ? rawRate / 100 : rawRate;
        await Promise.all(
          lineItems.map((item) => {
            const lineSub = Number(item.quantity) * Number(item.unitPrice);
            const lineDisc = Math.min(Number(item.discount) || 0, lineSub);
            const lineAfter = lineSub - lineDisc;
            const lineTaxVal = Number(item.tax) || 0;
            const effectiveLineTax = lineTaxVal > 0 ? lineTaxVal : lineAfter * taxRate;
            const lineTotal = lineAfter + effectiveLineTax;
            return InvoiceService.createItem({
              invoice: invoiceId,
              product: item.product?.id ?? undefined,
              description: item.description || (item.product?.attributes?.name ?? ''),
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discount: item.discount,
              tax: effectiveLineTax,
              total: lineTotal,
            });
          })
        );
      }

      setTimeout(() => {
        navigate('/invoices');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving invoice:', error);
      setError(
        error.response?.data?.error?.message ||
          'Failed to save invoice. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Invoice' : 'New Invoice'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Header Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Invoice Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {/* Invoice number is auto-generated (INV-YYYY-MM-####) — not shown on create */}
                <TextField
                  select
                  fullWidth
                  label="Invoice Type"
                  name="invoiceType"
                  value={formData.invoiceType || 'customer'}
                  onChange={(e) => {
                    handleChange(e);
                    // Reset selections when type changes
                    setSelectedInsuranceCompany(null);
                    setSelectedServiceProvider(null);
                    setSelectedCustomer(null);
                    setSelectedInsuranceClient(null);
                  }}
                  required
                >
                  <MenuItem value="insurance">Insurance</MenuItem>
                  <MenuItem value="service_provider">Service Provider</MenuItem>
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="insurance_customer">Insurance Customer</MenuItem>
                  <MenuItem value="service_provider_to_darb">Service Provider to Darb</MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="partiallyPaid">Partially Paid</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
              </Stack>

              {/* Insurance Company - for insurance type */}
              {formData.invoiceType === 'insurance' && (
                <Box>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Autocomplete
                      fullWidth
                      value={selectedInsuranceCompany}
                      isOptionEqualToValue={(option, value) => (option?.id ?? option?.attributes?.id) === (value?.id ?? value?.attributes?.id) || (option?.attributes?.name || option?.name) === (value?.attributes?.name || value?.name)}
                      onChange={(event, newValue) => {
                        setSelectedInsuranceCompany(newValue);
                        setFormData({ 
                          ...formData, 
                          insuranceCompany: newValue?.attributes?.name || newValue?.name || '',
                          insuranceClient: 0,
                          customer: 0,
                          serviceProvider: 0,
                        });
                      }}
                      options={insuranceCompanies}
                      loading={insuranceCompaniesLoading}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;
                        const searchLower = inputValue.toLowerCase();
                        return options.filter((option) => {
                          const attrs = option.attributes || option;
                          const name = (attrs.name || '').toLowerCase();
                          const code = (attrs.code || '').toLowerCase();
                          return name.includes(searchLower) || code.includes(searchLower);
                        });
                      }}
                      getOptionLabel={(option) => {
                        const attrs = option.attributes || option;
                        return attrs.name || '';
                      }}
                      renderOption={(props, option) => {
                        const attrs = option.attributes || option;
                        return (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body1">{attrs.name}</Typography>
                              {attrs.code && (
                                <Typography variant="caption" color="text.secondary">
                                  Code: {attrs.code}
                                </Typography>
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Insurance Company *"
                          required
                          placeholder="Search by name or code..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {insuranceCompaniesLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    {!selectedInsuranceCompany && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => window.open('/insurance-companies', '_blank')}
                        sx={{ minWidth: 200, height: 56 }}
                      >
                        Create new Insurance Company
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}

              {/* Service Provider - for service_provider type */}
              {formData.invoiceType === 'service_provider' && (
                <Box>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Autocomplete
                      fullWidth
                      value={selectedServiceProvider}
                      isOptionEqualToValue={(option, value) => (option?.id ?? option?.attributes?.id) === (value?.id ?? value?.attributes?.id)}
                      onChange={(event, newValue) => {
                        setSelectedServiceProvider(newValue);
                        setFormData({ 
                          ...formData, 
                          serviceProvider: newValue?.id || 0,
                          insuranceClient: 0,
                          customer: 0,
                          insuranceCompany: '',
                        });
                      }}
                      options={serviceProviders}
                      loading={serviceProvidersLoading}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;
                        const searchLower = inputValue.toLowerCase();
                        return options.filter((option) => {
                          const attrs = option.attributes || option;
                          const name = (attrs.name || '').toLowerCase();
                          const phone = (attrs.phone || '').toLowerCase();
                          const area = (attrs.area || '').toLowerCase();
                          return name.includes(searchLower) || phone.includes(searchLower) || area.includes(searchLower);
                        });
                      }}
                      getOptionLabel={(option) => {
                        const attrs = option.attributes || option;
                        return attrs.name || '';
                      }}
                      renderOption={(props, option) => {
                        const attrs = option.attributes || option;
                        return (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body1">{attrs.name}</Typography>
                              {attrs.phone && (
                                <Typography variant="caption" color="text.secondary">
                                  Phone: {attrs.phone}
                                </Typography>
                              )}
                              {attrs.area && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Area: {attrs.area}
                                </Typography>
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Service Provider *"
                          required
                          placeholder="Search by name, phone, or area..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {serviceProvidersLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    {!selectedServiceProvider && (
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => window.open('/service-providers', '_blank')}
                          sx={{ minWidth: 200, height: 56 }}
                        >
                          Create new Service Provider
                        </Button>
                      )}
                    </Stack>
                </Box>
              )}

              {/* Customer - for customer type */}
              {formData.invoiceType === 'customer' && (
                <Box>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Autocomplete
                      fullWidth
                      freeSolo
                      value={selectedCustomer}
                      isOptionEqualToValue={(option, value) => (option?.id ?? option?.attributes?.id) === (value?.id ?? value?.attributes?.id)}
                      onChange={(event, newValue) => {
                        if (typeof newValue === 'string') {
                          // Custom name entered
                          setSelectedCustomer(null);
                          setFormData({ 
                            ...formData, 
                            customer: 0,
                            customerName: newValue,
                            insuranceClient: 0,
                            serviceProvider: 0,
                            insuranceCompany: '',
                          });
                        } else {
                          setSelectedCustomer(newValue);
                          setFormData({ 
                            ...formData, 
                            customer: newValue?.id || 0,
                            customerName: undefined,
                            insuranceClient: 0,
                            serviceProvider: 0,
                            insuranceCompany: '',
                          });
                        }
                      }}
                      options={customers}
                      loading={customersLoading}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;
                        const searchLower = inputValue.toLowerCase();
                        return options.filter((option) => {
                          const attrs = option.attributes || option;
                          const companyName = (attrs.companyName || '').toLowerCase();
                          const contactPerson = (attrs.contactPerson || '').toLowerCase();
                          const phone = (attrs.phone || '').toLowerCase();
                          const email = (attrs.email || '').toLowerCase();
                          return companyName.includes(searchLower) || 
                                 contactPerson.includes(searchLower) || 
                                 phone.includes(searchLower) ||
                                 email.includes(searchLower);
                        });
                      }}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        const attrs = option.attributes || option;
                        return attrs.companyName || attrs.contactPerson || '';
                      }}
                      renderOption={(props, option) => {
                        const attrs = option.attributes || option;
                        return (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body1">
                                {attrs.companyName || attrs.contactPerson}
                              </Typography>
                              {attrs.phone && (
                                <Typography variant="caption" color="text.secondary">
                                  Phone: {attrs.phone}
                                </Typography>
                              )}
                              {attrs.email && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Email: {attrs.email}
                                </Typography>
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Customer *"
                          required
                          placeholder="Search or enter custom name..."
                          helperText="Select from list or enter a custom name"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {customersLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    {!selectedCustomer && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => window.open('/customers/create', '_blank')}
                        sx={{ minWidth: 200, height: 56 }}
                      >
                        Create new Customer
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}

              {/* Service Provider to Darb - shows Service Provider + Insurance Client */}
              {formData.invoiceType === 'service_provider_to_darb' && (
                <Box>
                  <Stack spacing={3}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Autocomplete
                        fullWidth
                        value={selectedServiceProvider}
                        isOptionEqualToValue={(option, value) => (option?.id ?? option?.attributes?.id) === (value?.id ?? value?.attributes?.id)}
                        onChange={(event, newValue) => {
                          setSelectedServiceProvider(newValue);
                          setFormData({ 
                            ...formData, 
                            serviceProvider: newValue?.id || 0,
                            insuranceClient: formData.insuranceClient,
                            customer: 0,
                            insuranceCompany: '',
                          });
                        }}
                        options={serviceProviders}
                        loading={serviceProvidersLoading}
                        filterOptions={(options, { inputValue }) => {
                          if (!inputValue) return options;
                          const searchLower = inputValue.toLowerCase();
                          return options.filter((option) => {
                            const attrs = option.attributes || option;
                            const name = (attrs.name || '').toLowerCase();
                            const phone = (attrs.phone || '').toLowerCase();
                            const area = (attrs.area || '').toLowerCase();
                            return name.includes(searchLower) || phone.includes(searchLower) || area.includes(searchLower);
                          });
                        }}
                        getOptionLabel={(option) => {
                          const attrs = option.attributes || option;
                          return attrs.name || '';
                        }}
                        renderOption={(props, option) => {
                          const attrs = option.attributes || option;
                          return (
                            <li {...props} key={option.id}>
                              <Box>
                                <Typography variant="body1">{attrs.name}</Typography>
                                {attrs.phone && (
                                  <Typography variant="caption" color="text.secondary">
                                    Phone: {attrs.phone}
                                  </Typography>
                                )}
                                {attrs.area && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Area: {attrs.area}
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Service Provider *"
                            required
                            placeholder="Search by name, phone, or area..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {serviceProvidersLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                      {!selectedServiceProvider && (
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => window.open('/service-providers', '_blank')}
                          sx={{ minWidth: 200, height: 56 }}
                        >
                          Create new Service Provider
                        </Button>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Autocomplete
                        fullWidth
                        value={selectedInsuranceClient}
                        isOptionEqualToValue={(option, value) => (option?.id ?? option?.attributes?.id) === (value?.id ?? value?.attributes?.id)}
                        inputValue={insuranceClientSearchInput}
                        onInputChange={(_, value, reason) => {
                          setInsuranceClientSearchInput(value);
                          if (reason === 'clear') {
                            if (insuranceClientSearchDebounceRef.current) {
                              clearTimeout(insuranceClientSearchDebounceRef.current);
                              insuranceClientSearchDebounceRef.current = null;
                            }
                            setInsuranceClientsSearchResults([]);
                            return;
                          }
                          if (insuranceClientSearchDebounceRef.current) {
                            clearTimeout(insuranceClientSearchDebounceRef.current);
                          }
                          insuranceClientSearchDebounceRef.current = setTimeout(() => {
                            insuranceClientSearchDebounceRef.current = null;
                            searchInsuranceClients(value);
                          }, 300);
                        }}
                        onChange={(event, newValue) => {
                          setSelectedInsuranceClient(newValue);
                          setFormData({ 
                            ...formData, 
                            insuranceClient: newValue?.id || 0,
                            serviceProvider: formData.serviceProvider,
                            customer: 0,
                            insuranceCompany: '',
                          });
                          if (!newValue) setInsuranceClientSearchInput('');
                        }}
                        options={insuranceClientSearchInput.trim()
                          ? insuranceClientsSearchResults
                          : insuranceClients}
                        loading={insuranceClientsLoading || insuranceClientsSearching}
                        filterOptions={(options) => options}
                        getOptionLabel={(option) => {
                          const attrs = option.attributes || option;
                          return attrs.customerName || attrs.vinNumber || '';
                        }}
                        renderOption={(props, option) => {
                          const attrs = option.attributes || option;
                          return (
                            <li {...props} key={option.id}>
                              <Box>
                                <Typography variant="body1">{attrs.customerName || attrs.vinNumber}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  VIN: {attrs.vinNumber}
                                </Typography>
                                {attrs.plateNumber && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Plate: {attrs.plateNumber}
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Insurance Client *"
                            required
                            placeholder="Search by name, VIN, phone, plate, or policy..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {(insuranceClientsLoading || insuranceClientsSearching) ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                      {!selectedInsuranceClient && (
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => window.open('/insurance-clients', '_blank')}
                          sx={{ minWidth: 200, height: 56 }}
                        >
                          Create new Insurance Client
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              )}

              {/* Insurance Customer - for insurance_customer type */}
              {formData.invoiceType === 'insurance_customer' && (
                <Box>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Autocomplete
                      fullWidth
                      value={selectedInsuranceClient}
                      isOptionEqualToValue={(option, value) => (option?.id ?? option?.attributes?.id) === (value?.id ?? value?.attributes?.id)}
                      inputValue={insuranceClientSearchInput}
                      onInputChange={(_, value, reason) => {
                        setInsuranceClientSearchInput(value);
                        if (reason === 'clear') {
                          if (insuranceClientSearchDebounceRef.current) {
                            clearTimeout(insuranceClientSearchDebounceRef.current);
                            insuranceClientSearchDebounceRef.current = null;
                          }
                          setInsuranceClientsSearchResults([]);
                          return;
                        }
                        if (insuranceClientSearchDebounceRef.current) {
                          clearTimeout(insuranceClientSearchDebounceRef.current);
                        }
                        insuranceClientSearchDebounceRef.current = setTimeout(() => {
                          insuranceClientSearchDebounceRef.current = null;
                          searchInsuranceClients(value);
                        }, 300);
                      }}
                      onChange={(event, newValue) => {
                        setSelectedInsuranceClient(newValue);
                        setFormData({ 
                          ...formData, 
                          insuranceClient: newValue?.id || 0,
                          customer: 0,
                          serviceProvider: 0,
                          insuranceCompany: '',
                        });
                        if (!newValue) setInsuranceClientSearchInput('');
                      }}
                      options={insuranceClientSearchInput.trim()
                        ? insuranceClientsSearchResults
                        : insuranceClients}
                      loading={insuranceClientsLoading || insuranceClientsSearching}
                      filterOptions={(options) => options}
                      getOptionLabel={(option) => {
                        const attrs = option.attributes || option;
                        return attrs.customerName || attrs.vinNumber || '';
                      }}
                      renderOption={(props, option) => {
                        const attrs = option.attributes || option;
                        return (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body1">{attrs.customerName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                VIN: {attrs.vinNumber}
                              </Typography>
                              {attrs.plateNumber && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Plate: {attrs.plateNumber}
                                </Typography>
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Insurance Customer *"
                          required
                          placeholder="Search by name, VIN, phone, plate, or policy..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {(insuranceClientsLoading || insuranceClientsSearching) ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    {!selectedInsuranceClient && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => window.open('/insurance-clients', '_blank')}
                        sx={{ minWidth: 200, height: 56 }}
                      >
                        Create new Insurance Client
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Invoice Date"
                  name="invoiceDate"
                  type="date"
                  value={formData.invoiceDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Due Date"
                  name="dueDate"
                  type="date"
                  value={formData.dueDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  helperText="Payment due date"
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <NumberTextField
                  fullWidth
                  label="Tax Rate"
                  name="taxRate"
                  value={formData.taxRate ?? 0.05}
                  onChange={handleChange}
                  onValueChange={(val) => setFormData((prev) => ({ ...prev, taxRate: val }))}
                  inputProps={{ min: 0, max: 1, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">%</InputAdornment>,
                  }}
                  helperText="Enter % (e.g. 5 for 5%) or decimal (0.05). Totals auto-update."
                />
                <NumberTextField
                  fullWidth
                  label="Other Charges"
                  name="other"
                  value={formData.other ?? 0}
                  onChange={handleChange}
                  onValueChange={(val) => setFormData((prev) => ({ ...prev, other: val }))}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">OMR</InputAdornment>,
                  }}
                  helperText="Additional charges. Totals auto-update."
                />
                <TextField
                  fullWidth
                  label="Oman Tax ID"
                  name="omanTaxId"
                  value={formData.omanTaxId ?? ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, omanTaxId: e.target.value }))}
                  placeholder="e.g. 1234567890"
                  helperText="Tax registration ID for this invoice (not a payment). Shown on PDF."
                />
              </Stack>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Internal notes..."
              />
            </Stack>
          </Paper>

          {/* Line Items */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Line Items</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addLineItem}
                size="small"
              >
                Add Item
              </Button>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="30%">Product</TableCell>
                    <TableCell width="10%">Qty</TableCell>
                    <TableCell width="15%">Unit Price</TableCell>
                    <TableCell width="12%">Discount</TableCell>
                    <TableCell width="12%">Tax</TableCell>
                    <TableCell width="15%">Total</TableCell>
                    <TableCell width="6%"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No items added. Click "Add Item" to start.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineItems.map((item) => {
                      const lineSub = Number(item.quantity) * Number(item.unitPrice);
                      const lineDisc = Math.min(Number(item.discount) || 0, lineSub);
                      const lineAfter = lineSub - lineDisc;
                      const rawRate = formData.taxRate ?? 0.05;
                      const taxRate = rawRate > 1 ? rawRate / 100 : rawRate;
                      const effectiveLineTax =
                        (Number(item.tax) || 0) > 0 ? Number(item.tax) : lineAfter * taxRate;
                      return (
                      <TableRow key={item.tempId}>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            value={item.product ?? item.description ?? null}
                            onChange={(e, newValue) =>
                              updateLineItem(item.tempId, 'product', newValue)
                            }
                            onInputChange={(e, value, reason) => {
                              if (reason === 'input' && typeof value === 'string') {
                                updateLineItem(item.tempId, 'product', value);
                              }
                            }}
                            options={products}
                            loading={productsLoading}
                            getOptionLabel={(option) =>
                              typeof option === 'string'
                                ? option
                                : option?.attributes?.name
                                  ? `${option.attributes.name} (${option.attributes?.sku || ''})`
                                  : option?.name || ''
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select product or type custom item"
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.quantity}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'quantity', e.target.value)
                            }
                            inputProps={{ min: 1, step: 1 }}
                            allowDecimal={false}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.unitPrice}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'unitPrice', e.target.value)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">OMR</InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.discount}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'discount', e.target.value)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">OMR</InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={effectiveLineTax}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'tax', e.target.value)
                            }
                            onValueChange={(val) => updateLineItem(item.tempId, 'tax', val)}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">OMR</InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            OMR {(lineAfter + effectiveLineTax).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeLineItem(item.tempId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ); })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Totals Summary */}
          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="h6">OMR {formData.subtotal.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" color="error">
                    Discount:
                  </Typography>
                  <Typography variant="h6" color="error">
                    - OMR {formData.discount.toFixed(2)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Tax:</Typography>
                  <Typography variant="h6">OMR {formData.tax.toFixed(2)}</Typography>
                </Stack>
                {(formData.other ?? 0) > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1">Other charges:</Typography>
                    <Typography variant="h6">OMR {(formData.other ?? 0).toFixed(2)}</Typography>
                  </Stack>
                )}
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h5">Total:</Typography>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    OMR {formData.total.toFixed(2)}
                  </Typography>
                </Stack>
                {formData.paidAmount > 0 && (
                  <>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body1" color="success.main">
                        Paid Amount:
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        OMR {formData.paidAmount.toFixed(2)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h5">Balance Due:</Typography>
                      <Typography variant="h4" color="error.main" fontWeight="bold">
                        OMR {formData.balanceDue.toFixed(2)}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/invoices')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit ? 'Update Invoice' : 'Create Invoice'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default InvoiceForm;
